/* eslint-disable react/no-this-in-sfc */
/* eslint-disable check-i18n-id-prefix/i18n-id-prefix-with-package-name-FormattedMessage */
import type { FC, ReactElement } from 'react';
import React, { PureComponent, useEffect, useContext, useRef } from 'react';
import type { WithIntlProps } from 'react-intl';
import { injectIntl, FormattedMessage } from 'react-intl';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import AkButton from '@atlaskit/button/button';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import Spinner from '@atlaskit/spinner/spinner';

import { EDIT_PAGE_REVERT_DRAFT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ChangesDiffLoader } from '@confluence/changes-diff';
import { ContentTypeType } from '@confluence/editor-features/entry-points/types.ts';
import { ALLOWED_FLAG_MESSAGES } from '@confluence/editor-flags/entry-points/allowedFlagConstants.ts';
import { EditorErrorFlags } from '@confluence/editor-flags/entry-points/EditorFlags.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';

import {
	ContributorAvatar,
	ContributorAvatarImg,
	ContributorDetail,
	ContributorDetailLastModification,
	ContributorDetailName,
	RevertContributorsList,
	RevertContributorsListItem,
	RevertLinkActions,
	RevertModalHeader,
} from './editorRevertDialogStyles.tsx';
import { SUBMIT_TEXT, WARN_TEXT } from './text.ts';
import type { EditorRevertDialogComponentClassProps, Relation } from './editorRevertDialogTypes.ts';

const JustifyShim = (props) => <span {...props} />;

const wrapperSpinnerStyles = xcss({
	marginBlock: 'space.600',
	marginInline: 'auto',
	textAlign: 'center',
});

const WrapperSpinner = (
	<Box xcss={wrapperSpinnerStyles}>
		<Spinner />
	</Box>
);

const RevertExperienceTracker: FC<{
	children: ({ experienceResolver }) => ReactElement;
	analyticsAttributes?: { [key: string]: any };
}> = ({ children, analyticsAttributes }) => {
	const experienceTracker: ExperienceTrackerAPI = useContext(ExperienceTrackerContext);
	const [spaceKey] = usePageSpaceKey();
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const { current: expTrackerConfig } = useRef({
		name: EDIT_PAGE_REVERT_DRAFT_EXPERIENCE,
		attributes: {
			...analyticsAttributes,
			isCompanyHub,
		},
	});

	useEffect(() => {
		experienceTracker.start(expTrackerConfig);

		return () =>
			experienceTracker.abort({
				...expTrackerConfig,
				reason: 'Modal closed',
			});
	}, [experienceTracker, expTrackerConfig]);

	return children({
		experienceResolver: (prom: Promise<any>): Promise<void> =>
			prom
				.then(() => experienceTracker.succeed(expTrackerConfig))
				.catch((error) => {
					experienceTracker.fail({
						...expTrackerConfig,
						error,
					});
					throw error;
				}),
	});
};

const closeButtonStyles = xcss({
	marginLeft: 'space.100',
});

const CloseButton = ({ children }) => (
	<Box as="span" xcss={closeButtonStyles}>
		{children}
	</Box>
);

const iconDiffNotShownStyles = xcss({
	transform: 'rotate(0deg)',
	transition: '0.2s',
});
const iconDiffShownStyles = xcss({
	transform: 'rotate(90deg)',
	transition: '0.2s',
});

const DiffExpandIcon = ({ isDiffShown, children }) => {
	return <Box xcss={isDiffShown ? iconDiffShownStyles : iconDiffNotShownStyles}>{children}</Box>;
};

class EditorRevertDialogComponentClass extends PureComponent<EditorRevertDialogComponentClassProps> {
	static defaultProps = {
		contentType: ContentTypeType.PAGE, // Setting default value to help with the type error since contentType can be potentially null
	};

	state = {
		submitting: false,
	};

	_handleRevertDialogDismissed = () => this.props.hideRevertDialog();

	_handleRevertDialogSubmitted =
		({ experienceResolver }) =>
		async () => {
			this.setState({
				submitting: true,
			});
			const { isUnpublishedDraft, deleteContent, revertContent, contentId, triggerAnalyticsEvent } =
				this.props;
			try {
				if (isUnpublishedDraft) {
					triggerAnalyticsEvent('deleteContent');
					await experienceResolver(deleteContent(contentId));
				} else {
					triggerAnalyticsEvent('revertContent');
					await experienceResolver(revertContent({ variables: { contentId } }));
				}
			} catch (_err) {
				// TODO show a helpful error message
			}
		};

	_renderHeader = () => {
		const { isUnpublishedDraft, contentType, intl } = this.props;

		const headerContent = isUnpublishedDraft
			? contentType === ContentTypeType.PAGE
				? intl.formatMessage({ id: 'editor.quit-dialog.title.page.delete' })
				: intl.formatMessage({ id: 'editor.quit-dialog.title.blogpost.delete' })
			: intl.formatMessage({ id: 'editor.quit-dialog.title.discard' });
		return <RevertModalHeader>{headerContent}</RevertModalHeader>;
	};

	renderWarningText = (contributorsList: Relation[]) => {
		const { isUnpublishedDraft, contentType } = this.props;

		const operationType = isUnpublishedDraft ? 'delete' : 'revert';
		const contributionType = contributorsList.length === 0 ? 'one' : 'many';
		const warningTextKey = WARN_TEXT[operationType][contentType!][contributionType];
		return <FormattedMessage id={warningTextKey} />;
	};

	_renderContributorsListSection = (contributorsList: Relation[]) => {
		const { intl } = this.props;

		return (
			<div>
				<RevertContributorsList>
					{contributorsList.map((contributor) => (
						<RevertContributorsListItem key={contributor?.source?.accountId}>
							<ContributorAvatar>
								<ContributorAvatarImg src={contributor?.source?.profilePicture?.path} alt="" />
							</ContributorAvatar>
							<ContributorDetail>
								<ContributorDetailName>
									<span>{contributor?.source?.displayName}</span>
								</ContributorDetailName>
								<ContributorDetailLastModification>
									<span>
										{intl.formatMessage({
											id: 'editor.quit-dialog.last.edited',
										})}{' '}
										{contributor?.relationData?.friendlyCreatedDate}
									</span>
								</ContributorDetailLastModification>
							</ContributorDetail>
						</RevertContributorsListItem>
					))}
				</RevertContributorsList>
			</div>
		);
	};

	_calculateContributorsList = (
		collaboratorRelations: Relation[],
		touchedRelations: Relation[],
	): Relation[] => {
		const { currentAccountId } = this.props;
		const collaboratorAccountIds = collaboratorRelations.map(
			(collaboratorRelation) => collaboratorRelation?.source?.accountId,
		);
		return touchedRelations.filter(
			(touchedRelation) =>
				collaboratorAccountIds.indexOf(touchedRelation?.source?.accountId) > -1 &&
				touchedRelation?.source?.accountId !== currentAccountId,
		);
	};

	_getDialogSubmitButtonText = () => {
		const { contentType, intl } = this.props;
		const submitTextKeyMessage = {
			id: SUBMIT_TEXT[this.getCurrentOperationType()][contentType!],
		};
		return intl.formatMessage(submitTextKeyMessage);
	};

	getCurrentOperationType = () => (this.props.isUnpublishedDraft ? 'delete' : 'revert');

	_renderShowDiffToggle = (isDiffShown) => {
		const { hideRevertDialogDiff, showRevertDialogDiff } = this.props;

		// If the diff is currently shown, the toggle action will HIDE the diff. The toggle action should also show the contributors,
		// though this functionality is currently broken (see CFE-3296).
		// If the diff is not currently shown, the toggle action will SHOW the diff.
		const toggleAction = isDiffShown ? hideRevertDialogDiff : showRevertDialogDiff;
		const toggleActionTextId = isDiffShown
			? 'editor.quit-dialog.link.hide.diff'
			: 'editor.quit-dialog.link.show.diff';

		return (
			<RevertLinkActions>
				<AkButton
					appearance="link"
					onClick={toggleAction}
					aria-expanded={isDiffShown}
					iconBefore={
						<DiffExpandIcon isDiffShown={isDiffShown}>
							<ChevronRightIcon spacing="spacious" label="Toggle diff" size="small" />
						</DiffExpandIcon>
					}
				>
					<FormattedMessage id={toggleActionTextId} />
				</AkButton>
			</RevertLinkActions>
		);
	};

	_renderChildren = () => {
		const {
			shouldShowRevertDialogDiff,
			contentId,
			isUnpublishedDraft,
			collaboratorRelations,
			touchedRelations,
		} = this.props;

		const contributorsList = this._calculateContributorsList(
			collaboratorRelations,
			touchedRelations,
		);

		const showDiffToggle = this._renderShowDiffToggle(shouldShowRevertDialogDiff);

		return (
			<div>
				<div>{this.renderWarningText(contributorsList)}</div>

				{!isUnpublishedDraft && showDiffToggle}

				{shouldShowRevertDialogDiff ? (
					<ChangesDiffLoader contentId={contentId} />
				) : (
					this._renderContributorsListSection(contributorsList)
				)}
			</div>
		);
	};

	_renderFooter =
		({ experienceResolver }) =>
		() => (
			<ModalFooter>
				<JustifyShim />
				<span>
					<span data-testid="submit-button">
						<AkButton
							isDisabled={this.state.submitting}
							iconAfter={this.state.submitting ? <Spinner /> : undefined}
							onClick={this._handleRevertDialogSubmitted({
								experienceResolver,
							})}
							appearance="primary"
						>
							{this._getDialogSubmitButtonText()}
						</AkButton>
					</span>
					<CloseButton>
						<AkButton
							isDisabled={this.state.submitting}
							onClick={this._handleRevertDialogDismissed}
							appearance="subtle"
						>
							<FormattedMessage id="close.name" />
						</AkButton>
					</CloseButton>
				</span>
			</ModalFooter>
		);

	render() {
		const {
			shouldShowRevertDialog,
			fetchingCollaboratorRelations,
			fetchingTouchedRelations,
			errorFetchingRelations,
			isUnpublishedDraft,
		} = this.props;

		if (!shouldShowRevertDialog) {
			return null;
		}

		if (errorFetchingRelations) {
			const errorData = {
				[ALLOWED_FLAG_MESSAGES.REVERT_CONTENT_ERROR]: true,
				status: null,
			};
			return <EditorErrorFlags errorData={errorData} />;
		}

		return (
			<RevertExperienceTracker analyticsAttributes={{ isUnpublishedDraft }}>
				{({ experienceResolver }) => (
					<div data-testid="editor-revert-modal-dialog">
						<ModalTransition>
							<Modal onClose={this._handleRevertDialogDismissed}>
								{this._renderHeader()}
								<ModalBody>
									{fetchingCollaboratorRelations || fetchingTouchedRelations
										? WrapperSpinner
										: this._renderChildren()}
								</ModalBody>
								{this._renderFooter({ experienceResolver })()}
							</Modal>
						</ModalTransition>
					</div>
				)}
			</RevertExperienceTracker>
		);
	}
}

export const EditorRevertDialogComponent: FC<
	WithIntlProps<EditorRevertDialogComponentClassProps>
> & {
	WrappedComponent: React.ComponentType<EditorRevertDialogComponentClassProps>;
} = injectIntl(EditorRevertDialogComponentClass);
